/**
 * GolfBoard Component
 *
 * Renders the 2x3 card grid, draw/discard piles, and handles all
 * player interactions during different turn phases.
 */

import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { GolfEngine } from '../GolfEngine';
import { Card, GridPosition, GolfMove, CardGrid, TurnPhase } from '../types';
import './GolfBoard.css';

export interface GolfBoardProps {
  engine: GolfEngine;
  playerIndex: number;
  interactive: boolean;
  isAIThinking?: boolean;
  onMove?: (move: GolfMove) => void;
  onInitialReveal?: (positions: GridPosition[]) => void;
  showAllPlayers?: boolean;
  animationSpeed?: number;
}

interface CardAnimation {
  type: 'draw' | 'place' | 'flip' | 'discard';
  from?: { x: number; y: number };
  to?: { x: number; y: number };
  position?: GridPosition;
}

const GolfBoard: React.FC<GolfBoardProps> = ({
  engine,
  playerIndex,
  interactive,
  isAIThinking = false,
  onMove,
  onInitialReveal,
  showAllPlayers = false,
  animationSpeed = 400,
}) => {
  const [selectedInitialCards, setSelectedInitialCards] = useState<GridPosition[]>([]);
  const [drawnCard, setDrawnCard] = useState<Card | null>(null);
  const [animatingCard, setAnimatingCard] = useState<CardAnimation | null>(null);
  const [highlightedPositions, setHighlightedPositions] = useState<GridPosition[]>([]);

  const gameState = engine.getGameState();
  const roundPhase = gameState.roundPhase;
  const turnPhase = gameState.turnPhase;
  const isMyTurn = gameState.currentPlayerIndex === playerIndex;
  const playerGrid = gameState.players[playerIndex]?.grid;
  const discardTop = engine.getDiscardPileTop();
  const drawPileCount = engine.getDrawPileCount();

  // Determine the current interaction phase
  const phase = useMemo(() => {
    if (!interactive || isAIThinking) return 'waiting';
    if (roundPhase === 'initial-reveal' && !engine.hasCompletedInitialReveal(playerIndex)) {
      return 'initial-reveal';
    }
    if (!isMyTurn) return 'waiting';
    if (roundPhase === 'playing' || roundPhase === 'final-turns') {
      return turnPhase === 'draw' ? 'draw' : turnPhase === 'place-or-discard' ? 'place-or-discard' : turnPhase === 'flip' ? 'flip' : 'waiting';
    }
    return 'waiting';
  }, [interactive, isAIThinking, roundPhase, turnPhase, isMyTurn, playerIndex, engine]);

  // Calculate column-pair highlights
  const columnPairHighlights = useMemo(() => {
    if (!playerGrid) return [];
    const highlights: number[] = [];
    for (let col = 0; col < 3; col++) {
      const top = playerGrid[0][col];
      const bottom = playerGrid[1][col];
      if (top.faceUp && bottom.faceUp && top.card.rank === bottom.card.rank) {
        highlights.push(col);
      }
    }
    return highlights;
  }, [playerGrid]);

  // Handle initial card reveal
  const handleInitialReveal = useCallback((position: GridPosition) => {
    if (phase !== 'initial-reveal') return;
    if (playerGrid && playerGrid[position.row][position.col].faceUp) return;

    const alreadySelected = selectedInitialCards.some(
      p => p.row === position.row && p.col === position.col
    );

    if (alreadySelected) {
      setSelectedInitialCards(prev => prev.filter(
        p => !(p.row === position.row && p.col === position.col)
      ));
      return;
    }

    const newSelection = [...selectedInitialCards, position];
    setSelectedInitialCards(newSelection);

    if (newSelection.length === 2) {
      // Animate flips
      setAnimatingCard({ type: 'flip', position: newSelection[0] });
      setTimeout(() => {
        setAnimatingCard({ type: 'flip', position: newSelection[1] });
        setTimeout(() => {
          setAnimatingCard(null);
          if (onInitialReveal) {
            onInitialReveal(newSelection);
          }
          if (onMove) {
            onMove({ type: 'initial-reveal', positions: newSelection });
          }
          setSelectedInitialCards([]);
        }, animationSpeed);
      }, animationSpeed);
    }
  }, [phase, playerGrid, selectedInitialCards, onInitialReveal, onMove, animationSpeed]);

  // Handle draw from pile
  const handleDraw = useCallback((source: 'draw-pile' | 'discard-pile') => {
    if (phase !== 'draw') return;

    setAnimatingCard({ type: 'draw' });
    setTimeout(() => {
      try {
        const card = engine.drawCard(source);
        setDrawnCard(card);
        setAnimatingCard(null);
        if (onMove) {
          onMove({ type: 'draw', source });
        }
      } catch {
        setAnimatingCard(null);
      }
    }, animationSpeed);
  }, [phase, engine, onMove, animationSpeed]);

  // Handle replacing a grid card with drawn card
  const handleReplace = useCallback((position: GridPosition) => {
    if (phase !== 'place-or-discard' || !drawnCard) return;

    setAnimatingCard({ type: 'place', position });
    setTimeout(() => {
      const result = engine.replaceCard(position);
      if (result.success) {
        if (onMove) {
          onMove({
            type: 'replace',
            position,
            drawnCard: drawnCard,
            replacedCard: result.replacedCard!,
          });
        }
      }
      setDrawnCard(null);
      setAnimatingCard(null);
    }, animationSpeed);
  }, [phase, drawnCard, engine, onMove, animationSpeed]);

  // Handle discarding drawn card
  const handleDiscard = useCallback(() => {
    if (phase !== 'place-or-discard' || !drawnCard) return;

    setAnimatingCard({ type: 'discard' });
    setTimeout(() => {
      engine.discardDrawnCard();
      const discardedCard = drawnCard;
      setDrawnCard(null);
      setAnimatingCard(null);
    }, animationSpeed);
  }, [phase, drawnCard, engine, animationSpeed]);

  // Handle flipping a face-down card
  const handleFlip = useCallback((position: GridPosition) => {
    if (phase !== 'flip') return;
    if (playerGrid && playerGrid[position.row][position.col].faceUp) return;

    setAnimatingCard({ type: 'flip', position });
    setTimeout(() => {
      const success = engine.flipCard(position);
      if (success && onMove) {
        onMove({
          type: 'discard',
          drawnCard: discardTop!,
          flippedPosition: position,
        });
      }
      setAnimatingCard(null);
    }, animationSpeed);
  }, [phase, playerGrid, engine, onMove, discardTop, animationSpeed]);

  // Handle card click based on current phase
  const handleCardClick = useCallback((position: GridPosition) => {
    switch (phase) {
      case 'initial-reveal':
        handleInitialReveal(position);
        break;
      case 'place-or-discard':
        handleReplace(position);
        break;
      case 'flip':
        handleFlip(position);
        break;
      default:
        break;
    }
  }, [phase, handleInitialReveal, handleReplace, handleFlip]);

  // Get card display content
  const getCardDisplay = (card: Card): string => {
    const suitSymbols: Record<string, string> = {
      hearts: '♥',
      diamonds: '♦',
      clubs: '♣',
      spades: '♠',
    };
    return `${card.rank}${suitSymbols[card.suit]}`;
  };

  const getCardColor = (card: Card): string => {
    return card.suit === 'hearts' || card.suit === 'diamonds' ? 'red' : 'black';
  };

  // Render a single card cell
  const renderCard = (row: number, col: number) => {
    if (!playerGrid) return null;
    const cell = playerGrid[row][col];
    const position: GridPosition = { row, col };
    const isColumnPair = columnPairHighlights.includes(col);
    const isSelected = selectedInitialCards.some(p => p.row === row && p.col === col);
    const isAnimating = animatingCard?.position?.row === row && animatingCard?.position?.col === col;

    const isInteractive =
      (phase === 'initial-reveal' && !cell.faceUp) ||
      (phase === 'place-or-discard' && drawnCard !== null) ||
      (phase === 'flip' && !cell.faceUp);

    const classNames = [
      'golf-board__card',
      cell.faceUp ? 'golf-board__card--face-up' : 'golf-board__card--face-down',
      isColumnPair ? 'golf-board__card--column-pair' : '',
      isSelected ? 'golf-board__card--selected' : '',
      isAnimating ? 'golf-board__card--animating' : '',
      isInteractive ? 'golf-board__card--interactive' : '',
    ].filter(Boolean).join(' ');

    return (
      <button
        key={`${row}-${col}`}
        className={classNames}
        onClick={() => handleCardClick(position)}
        disabled={!isInteractive}
        aria-label={
          cell.faceUp
            ? `Card at row ${row + 1}, column ${col + 1}: ${getCardDisplay(cell.card)}`
            : `Face-down card at row ${row + 1}, column ${col + 1}`
        }
        style={{ '--animation-speed': `${animationSpeed}ms` } as React.CSSProperties}
      >
        {cell.faceUp ? (
          <span className={`golf-board__card-value golf-board__card-value--${getCardColor(cell.card)}`}>
            {getCardDisplay(cell.card)}
          </span>
        ) : (
          <span className="golf-board__card-back">🂠</span>
        )}
      </button>
    );
  };

  // Render opponent grids (for showAllPlayers mode)
  const renderOpponentGrids = () => {
    if (!showAllPlayers) return null;

    return gameState.players
      .map((player, idx) => {
        if (idx === playerIndex) return null;
        return (
          <div key={idx} className="golf-board__opponent">
            <h4 className="golf-board__opponent-name">
              {gameState.playerNames[idx]}
            </h4>
            <div className="golf-board__grid golf-board__grid--opponent">
              {[0, 1].map(row => (
                <div key={row} className="golf-board__row">
                  {[0, 1, 2].map(col => {
                    const cell = player.grid[row][col];
                    return (
                      <div
                        key={`${row}-${col}`}
                        className={`golf-board__card golf-board__card--small ${
                          cell.faceUp ? 'golf-board__card--face-up' : 'golf-board__card--face-down'
                        }`}
                      >
                        {cell.faceUp ? (
                          <span className={`golf-board__card-value golf-board__card-value--${getCardColor(cell.card)}`}>
                            {getCardDisplay(cell.card)}
                          </span>
                        ) : (
                          <span className="golf-board__card-back">🂠</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        );
      })
      .filter(Boolean);
  };

  return (
    <div className="golf-board" role="region" aria-label="Golf card game board">
      {/* Status bar */}
      <div className="golf-board__status">
        {phase === 'initial-reveal' && (
          <p className="golf-board__instruction">
            Select 2 cards to reveal ({selectedInitialCards.length}/2 selected)
          </p>
        )}
        {phase === 'draw' && (
          <p className="golf-board__instruction">
            Draw a card from the draw pile or discard pile
          </p>
        )}
        {phase === 'place-or-discard' && (
          <p className="golf-board__instruction">
            Replace a card in your grid or discard the drawn card
          </p>
        )}
        {phase === 'flip' && (
          <p className="golf-board__instruction">
            Flip a face-down card
          </p>
        )}
        {phase === 'waiting' && isAIThinking && (
          <p className="golf-board__instruction golf-board__instruction--thinking">
            AI is thinking...
          </p>
        )}
        {phase === 'waiting' && !isAIThinking && !isMyTurn && (
          <p className="golf-board__instruction">
            Waiting for {gameState.playerNames[gameState.currentPlayerIndex]}'s turn
          </p>
        )}
      </div>

      {/* Piles area */}
      <div className="golf-board__piles">
        <button
          className={`golf-board__pile golf-board__pile--draw ${phase === 'draw' ? 'golf-board__pile--active' : ''}`}
          onClick={() => handleDraw('draw-pile')}
          disabled={phase !== 'draw'}
          aria-label={`Draw pile: ${drawPileCount} cards remaining`}
        >
          <span className="golf-board__pile-back">🂠</span>
          <span className="golf-board__pile-count">{drawPileCount}</span>
        </button>

        <button
          className={`golf-board__pile golf-board__pile--discard ${phase === 'draw' ? 'golf-board__pile--active' : ''} ${phase === 'place-or-discard' ? 'golf-board__pile--discard-target' : ''}`}
          onClick={() => {
            if (phase === 'draw') {
              handleDraw('discard-pile');
            } else if (phase === 'place-or-discard') {
              handleDiscard();
            }
          }}
          disabled={phase !== 'draw' && phase !== 'place-or-discard'}
          aria-label={
            discardTop
              ? `Discard pile: top card is ${getCardDisplay(discardTop)}`
              : 'Discard pile: empty'
          }
        >
          {discardTop ? (
            <span className={`golf-board__card-value golf-board__card-value--${getCardColor(discardTop)}`}>
              {getCardDisplay(discardTop)}
            </span>
          ) : (
            <span className="golf-board__pile-empty">Empty</span>
          )}
        </button>
      </div>

      {/* Drawn card in hand */}
      {drawnCard && (
        <div className="golf-board__hand" aria-label={`Card in hand: ${getCardDisplay(drawnCard)}`}>
          <div className={`golf-board__hand-card golf-board__card--face-up`}>
            <span className={`golf-board__card-value golf-board__card-value--${getCardColor(drawnCard)}`}>
              {getCardDisplay(drawnCard)}
            </span>
          </div>
          <p className="golf-board__hand-label">In Hand</p>
        </div>
      )}

      {/* Player's card grid */}
      <div className="golf-board__grid-container">
        <h3 className="golf-board__player-name">
          {gameState.playerNames[playerIndex]}
        </h3>
        <div className="golf-board__grid" role="grid" aria-label="Your card grid">
          {[0, 1].map(row => (
            <div key={row} className="golf-board__row" role="row">
              {[0, 1, 2].map(col => renderCard(row, col))}
            </div>
          ))}
        </div>
      </div>

      {/* Opponent grids */}
      {showAllPlayers && (
        <div className="golf-board__opponents">
          {renderOpponentGrids()}
        </div>
      )}
    </div>
  );
};

export default GolfBoard;
